import React from 'react'
import { createPortal } from 'react-dom'
import { GridCell } from '@mobi/component-library/Common/Grid'
import { observeImmutable } from '@core/Components/HOCs/observe'
import { LoadingPlaceholder } from '@mobi/component-library/Common/LoadingPlaceholder'
import {
  state$ as userState$,
  UserAccountState,
  UserAccountStateRecord,
} from '@core/State/UserAccount/userAccountDriver'
import { BonusSubHeaderContainerStyled, OfferCountAggregateDivStyled } from '../BetOfferPage.styles'
import { BetOfferItem } from './BetOfferItem'
import { trackOptimoveEvent } from '@core/Services/Optimove/optimove'
import { TabButton, Tabs } from '@mobi/component-library/Navigation/Tabs'
import { BonusBetOrCashItem } from './BonusBetOrCashItem'
import { Button, Icon } from '@mobi/component-library/Common/V2'
import { hexColors } from '@mobi/settings'
import { setBonusDescriptionToDisplay } from '@core/Areas/AppHeader/Store'
import { useAppDispatch } from '@core/Store/hooks'
import { BonusOffersEmptyState } from './BonusOffersEmptyState'
import { toMoney } from '@mobi/utils/money'
import { useFeature } from '@core/Utils/hooks'
import { pluralize } from '@mobi/utils/string/pluralize'
import { FeatureFlags } from '@mobi/settings/featureFlags'
import { BonusBetSplittingTray, useSplitBonusBetMutation } from '../../../Bonuses'
import { groupBonusBets, isCampaignV2 } from '@core/Areas/Bonuses/data/helpers'
import { CampaignV2 } from '@core/Areas/Bonuses/data/types'
import {
  trackBonusBetsInfoClick,
  trackBonusCashInfoClick,
  trackBonusOfferClick,
  trackBonusScreenLoaded,
  trackBonusTabChange,
} from '@core/Areas/Bonuses/analytics'
import type { Campaign } from '@mobi/account/Areas/SignUp'

type BetOffersProps = Pick<UserAccountState, 'isLoggedIn' | 'campaigns'> & {
  handleCampaignClick: (campaign: Campaign) => void
}

export const BetOffersInternal: React.FC<BetOffersProps> = ({
  handleCampaignClick,
  isLoggedIn,
  campaigns,
}) => {
  React.useEffect(() => {
    trackOptimoveEvent({
      eventName: 'visited_and_has_bonus_offer',
      data: { has_bonus_offer: !!(campaigns && campaigns.count() > 0) },
    })
  }, [campaigns])

  const dispatch = useAppDispatch()
  const [selectedTab, setSelectedTab] = React.useState<BetOfferTabs>('Bonus Offers')

  const isSplitBonusFeatureActive = useFeature(FeatureFlags.SPLIT_BONUS_BETS.key)
  const isCampaignsV2FeatureActive = useFeature(FeatureFlags.GET_CAMPAIGNS_V2.key)

  const [bonusBetToSplit, setBonusBetToSplit] = React.useState<CampaignV2 | null>(null)

  const isSplitBonusBetTrayOpen = !!bonusBetToSplit

  const { mutate, isPending } = useSplitBonusBetMutation({
    updateBonusBetToSplit: () => setBonusBetToSplit(null),
  })

  const offers =
    campaigns
      ?.filter(c => c.isActivationPending || (!c.isExpired && c.isLive && !c.isActivated))
      .toArray() ?? []

  const bonusBets =
    campaigns
      ?.filter(c => c.rewardType === 'BonusBetReward' && c.isActivated && !c.isExpired)
      .toArray() ?? []

  const groupedBonusBets =
    bonusBets && isCampaignsV2FeatureActive && isSplitBonusFeatureActive
      ? groupBonusBets(bonusBets)
      : {}

  const bonusCash =
    campaigns
      ?.filter(c => c.rewardType === 'BonusCashReward' && c.isActivated && !c.isExpired)
      .toArray() ?? []

  const getBonusCount = (bonusType: string) => {
    switch (bonusType) {
      case 'Bonus Offers':
        return offers.length.toString()
      case 'Bonus Bets':
        return bonusBets.length.toString()
      case 'Bonus Cash':
        return bonusCash.length.toString()
      default:
        return undefined
    }
  }

  const createTabClickHandler = (tab: BetOfferTabs) => () => {
    setSelectedTab(tab)
    trackBonusTabChange(tab, getBonusCount(tab))
  }

  // Run once when component first renders
  React.useEffect(() => {
    trackBonusScreenLoaded(selectedTab, getBonusCount(selectedTab))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!isLoggedIn) {
    return null
  }

  if (!campaigns) {
    return (
      <GridCell padding='1rem' data-tid-loading>
        <LoadingPlaceholder width='100%' height='4rem' />
      </GridCell>
    )
  }

  const showBonusCashDescription = () => {
    dispatch(setBonusDescriptionToDisplay('bonus_cash'))
    trackBonusCashInfoClick('Tab Content')
  }

  const showBonusBetDescription = () => {
    dispatch(setBonusDescriptionToDisplay('bonus_bet'))
    trackBonusBetsInfoClick('Tab Content')
  }

  return (
    <>
      <Tabs>
        <TabButton
          content={
            <>
              Bonus Offers
              {offers.length ? (
                <OfferCountAggregateDivStyled>{offers.length}</OfferCountAggregateDivStyled>
              ) : null}
            </>
          }
          onClick={createTabClickHandler('Bonus Offers')}
          isSelected={selectedTab === 'Bonus Offers'}
          shouldFillSpace={true}
        />

        <TabButton
          content={
            <>
              Bonus Bets
              {bonusBets.length ? (
                <OfferCountAggregateDivStyled>{bonusBets.length}</OfferCountAggregateDivStyled>
              ) : null}
            </>
          }
          onClick={createTabClickHandler('Bonus Bets')}
          isSelected={selectedTab === 'Bonus Bets'}
          shouldFillSpace={true}
        />

        <TabButton
          content={
            <>
              Bonus Cash
              {bonusCash.length ? (
                <OfferCountAggregateDivStyled>{bonusCash.length}</OfferCountAggregateDivStyled>
              ) : null}
            </>
          }
          onClick={createTabClickHandler('Bonus Cash')}
          isSelected={selectedTab === 'Bonus Cash'}
          shouldFillSpace={true}
        />
      </Tabs>

      {selectedTab === 'Bonus Bets' && !!bonusBets.length ? (
        <BonusSubHeaderContainerStyled>
          <Button color='tertiary_grey' onClick={showBonusBetDescription} size='xs'>
            {bonusBets.length} {pluralize(bonusBets.length, 'Bonus Bet')}
            <Icon name='LineHelpCircle' size='1.4rem' color={hexColors.grey} />
          </Button>
          <p>Total: {toMoney(bonusBets.reduce((sum, bonus) => sum + bonus.remainingAmount, 0))}</p>
        </BonusSubHeaderContainerStyled>
      ) : null}

      {selectedTab === 'Bonus Cash' && !!bonusCash.length ? (
        <BonusSubHeaderContainerStyled>
          <Button color='tertiary_grey' onClick={showBonusCashDescription} size='xs'>
            Bonus Cash
            <Icon name='LineHelpCircle' size='1.4rem' color={hexColors.grey} />
          </Button>
          <p>Total: {toMoney(bonusCash.reduce((sum, bonus) => sum + bonus.remainingAmount, 0))}</p>
        </BonusSubHeaderContainerStyled>
      ) : null}

      <GridCell padding='0.4rem 0' data-tid-bonus-detail-list>
        {selectedTab === 'Bonus Offers' ? (
          offers.length === 0 ? (
            <BonusOffersEmptyState
              model={{ mode: 'BonusOffer', onShowHelpDetailsClicked: null }}
              data-tid-nooffers
            />
          ) : (
            offers.map(campaign => (
              <BetOfferItem
                key={campaign.id}
                data-tid-campaign-entry
                campaign={campaign}
                handleCampaignClick={handleCampaignClick}
              />
            ))
          )
        ) : null}

        {selectedTab === 'Bonus Bets' ? (
          bonusBets.length === 0 ? (
            <BonusOffersEmptyState
              model={{ mode: 'BonusBet', onShowHelpDetailsClicked: showBonusBetDescription }}
              data-tid-nooffers
            />
          ) : groupedBonusBets && isCampaignsV2FeatureActive && isSplitBonusFeatureActive ? (
            Object.keys(groupedBonusBets).map(key => (
              <BonusBetItem
                key={groupedBonusBets[key][0].id}
                item={groupedBonusBets[key][0]}
                setBonusBetToSplit={setBonusBetToSplit}
                betCount={groupedBonusBets[key].length}
              />
            ))
          ) : (
            bonusBets.map(item => <BonusBetItem key={item.id} item={item} />)
          )
        ) : null}

        {selectedTab === 'Bonus Cash' ? (
          bonusCash.length === 0 ? (
            <BonusOffersEmptyState
              model={{ mode: 'BonusCash', onShowHelpDetailsClicked: showBonusCashDescription }}
              data-tid-nooffers
            />
          ) : (
            bonusCash.map(item => (
              <BonusBetOrCashItem
                key={item.id}
                model={{
                  mode: 'BonusCash',
                  remainingAmount: item.remainingAmount,
                  expiry: item.expiry,
                }}
              />
            ))
          )
        ) : null}
      </GridCell>
      {isSplitBonusFeatureActive
        ? createPortal(
            <BonusBetSplittingTray
              bonusBetToSplit={bonusBetToSplit}
              isTrayOpen={isSplitBonusBetTrayOpen}
              closeTray={() => setBonusBetToSplit(null)}
              confirmSplitBonusBet={mutate}
              isBonusBetSplitPending={isPending}
            />,
            document.body
          )
        : null}
    </>
  )
}

export const BetOffers = observeImmutable<
  UserAccountStateRecord,
  { handleCampaignClick: (campaign: Campaign) => void }
>(userState$, ({ record, handleCampaignClick }) => {
  return (
    <BetOffersInternal
      isLoggedIn={record.isLoggedIn}
      campaigns={record.campaigns}
      handleCampaignClick={handleCampaignClick}
    />
  )
})

type BetOfferTabs = 'Bonus Offers' | 'Bonus Bets' | 'Bonus Cash'

const BonusBetItem = ({
  item,
  setBonusBetToSplit,
  betCount,
}: {
  item: Campaign
  setBonusBetToSplit?: React.Dispatch<React.SetStateAction<CampaignV2 | null>>
  betCount?: number
}) => {
  return (
    <BonusBetOrCashItem
      model={{
        mode: 'BonusBet',
        remainingAmount: item.remainingAmount,
        expiry: item.expiry,
      }}
      {...(isCampaignV2(item) && {
        canBeSplit: item.tokenCanBeSplit || undefined,
        betsRemaining: betCount,
        openSplitBonusBetTray: () => {
          setBonusBetToSplit && setBonusBetToSplit(item)
          trackBonusOfferClick(item, 'Split Bonus Bet')
        },
      })}
    />
  )
}
